import type { Media } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { payloadClient } from '.';
import type { FindPayloadApiClientParams } from './types';

export type MediaQueryParams = FindPayloadApiClientParams<'media', Media> & { disabled?: boolean };

export const useMedia = (params?: MediaQueryParams) => {
  return useQuery({
    queryKey: ['media', params],
    enabled: !params?.disabled,
    queryFn: async () => {
      const response = await payloadClient.find({
        collection: 'media',
        ...(params ? params : {}),
      });
      return response;
    },
  });
};

export const useMediaItem = (id: string, params?: { disabled?: boolean }) => {
  return useQuery({
    queryKey: ['media', id, params],
    enabled: !params?.disabled && !!id,
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'media',
        id,
      });
      return response;
    },
  });
};

export const useMediaBlobUrl = (mediaId: string | null) => {
  return useQuery({
    queryKey: ['media-blob', mediaId],
    enabled: !!mediaId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    queryFn: async () => {
      if (!mediaId) return null;

      const mediaData = await payloadClient.findById({
        collection: 'media',
        id: mediaId,
      });

      if (!mediaData?.url) return null;

      const request = await payloadClient.createRequest(mediaData.url.replace('/api', ''), {
        method: 'GET',
      });
      const response = await payloadClient.fetcher(request);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
  });
};

export const useUploadMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, data }: { file: File; data?: Partial<Media> }) => {
      const response = await payloadClient.create({
        collection: 'media',
        data: data || {},
        file,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });
};

export const useUpdateMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Media> }) => {
      const response = await payloadClient.updateById({
        collection: 'media',
        id,
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });
};

export const useDeleteMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await payloadClient.deleteById({
        collection: 'media',
        id,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });
};
